/**
 * WarehouseDetailPage — one warehouse's record, its supply chain and its
 * reorder points, each in a collapsible section.
 *
 * Layout only: the record is `useWarehouseRecord` and the three edits on it are
 * `useWarehouseMutations`.
 */
import { Loader2, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import WarehouseBasicInfoForm from './components/WarehouseBasicInfoForm';
import WarehouseDetailAlerts from './components/WarehouseDetailAlerts';
import WarehouseDetailHeader from './components/WarehouseDetailHeader';
import WarehouseReorderSection from './components/WarehouseReorderSection';
import WarehouseRestockSource from './components/WarehouseRestockSource';
import WarehouseSectionCard from './components/WarehouseSectionCard';
import { useWarehouseMutations } from './useWarehouseMutations';
import { useWarehouseRecord } from './useWarehouseRecord';

const WarehouseDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const record = useWarehouseRecord(id);
  const edits = useWarehouseMutations(record);
  const [expanded, setExpanded] = useState({ basicInfo: true, restock: true, reorder: true });
  const toggle = (key: keyof typeof expanded) => () =>
    setExpanded((p) => ({ ...p, [key]: !p[key] }));

  if (record.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={20} className="text-moss-600 animate-spin" />
        <span className="ml-2">Loading warehouse...</span>
      </div>
    );
  }

  const { warehouse } = record;
  if (!warehouse) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={32} className="text-red-600 mx-auto mb-3" />
        <p className="text-stone-600">Warehouse not found</p>
        <button
          onClick={() => navigate('/admin/settings/warehouses')}
          className="mt-4 text-moss-600 hover:text-moss-700"
        >
          Back to Warehouses
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WarehouseDetailHeader name={warehouse.name} code={warehouse.code} />
      <WarehouseDetailAlerts error={record.error} success={edits.success} />

      <div className="space-y-4">
        <WarehouseSectionCard
          title="Basic Information"
          expanded={expanded.basicInfo}
          onToggle={toggle('basicInfo')}
        >
          <WarehouseBasicInfoForm
            formData={record.formData}
            setFormData={record.setFormData}
            saving={edits.saving}
            onSave={() => void edits.saveBasicInfo()}
            onDeactivate={() => void edits.deactivate()}
          />
        </WarehouseSectionCard>

        <WarehouseSectionCard
          title="Supply Chain"
          expanded={expanded.restock}
          onToggle={toggle('restock')}
        >
          <WarehouseRestockSource
            options={record.restockOptions}
            selectedId={record.selectedRestockSourceId}
            saving={edits.saving}
            onChange={(next) => void edits.changeRestockSource(next)}
          />
        </WarehouseSectionCard>

        <WarehouseSectionCard
          title="Reorder Points"
          expanded={expanded.reorder}
          onToggle={toggle('reorder')}
        >
          <WarehouseReorderSection warehouse={warehouse} onRefresh={record.loadData} />
        </WarehouseSectionCard>
      </div>
    </div>
  );
};

export default WarehouseDetailPage;
